<template>
<div class="language-settings" :class="{ 'minimized': hasConnection }">
    <language-dropdown :value="sourceLanguage" :modelValue="sourceLanguage" :placeholder="$t('chooseLanguage')" @update:modelValue="$emit('update:sourceLanguage', $event)" :languages="speakingLanguages" :disabled="isListening" />

    <language-dropdown :value="targetLanguage" :modelValue="targetLanguage" :placeholder="$t('chooseLanguageTranslate')" @update:modelValue="$emit('update:targetLanguage', $event)" :languages="languages" :disabled="isListening" />

    <div class="control-container">
        <div class="mic-selection">
            <AudioInputDropdown :value="inputDevice" :modelValue="inputDevice" @update:modelValue="$emit('update:inputDevice', $event)" :disabled="isListening" />
        </div>
        <RecordButton :is-listening="isListening" :is-connecting="isConnecting" :connect-failed="connectFailed" :target-language="targetLanguage" :source-language="sourceLanguage" @click="handleButtonClick" />
        
    </div>

    <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
        <button @click="$emit('retry-connection')" class="retry-button">Retry</button>
    </div>
    <div class="toggles-row">
         <div class="diarization-toggle">
            <div class="toggle-container">
                <label class="circle-checkbox">
                    <input type="checkbox" :checked="diarizationEnabled" @change="$emit('update:diarizationEnabled', $event.target.checked)">
                    <span class="checkmark"></span>
                </label>
                <span class="toggle-label">{{ $t('speakerDiarization') }}</span>
            </div>
        </div>
        <div class="smooth-scroll-toggle">
            <div class="toggle-container">
                <label class="circle-checkbox">
                    <input type="checkbox" :checked="smoothScrollEnabled" @change="$emit('update:smoothScrollEnabled', $event.target.checked)">
                    <span class="checkmark"></span>
                </label>
                <span class="toggle-label">{{ $t('smoothScroll') }}</span>
            </div>
        </div>
    </div>
</div>
<div class="qr-code-container">
    <img :src="qrCodeUrl" alt="Share Room QR Code" class="qr-code" v-if="qrCodeUrl" />
    <div class="url-container">
        <input type="text" :value="'🔗' + shareUrl" readonly class="url-input" title="Readonly URL" />
        <button @click="copyUrl" class="copy-button" :class="{ 'copied': isCopied }">
            <svg v-if="!isCopied" class="icon" width="24" height="24" viewBox="0 0 24 24">
                <path :d="mdiContentCopy" fill="currentColor" />
            </svg>
            <svg v-else class="icon" width="24" height="24" viewBox="0 0 24 24">
                <path :d="mdiCheck" fill="currentColor" />
            </svg>
        </button>
        <button @click="openUrl" class="copy-button">
            <svg class="icon" width="24" height="24" viewBox="0 0 24 24">
                <path :d="mdiOpenInNew" fill="currentColor" />
            </svg>
        </button>
    </div>
</div>
</template>

<script>
import LanguageDropdown from '@/components/SpeakerLanguageDropdown.vue';
import AudioInputDropdown from '@/components/AudioInputDropdown.vue';
import RecordButton from '@/components/RecordButton.vue';
import {
    mdiContentCopy,
    mdiCheck,
    mdiLink,
    mdiOpenInNew
} from '@mdi/js';

export default {
    name: 'SpeakerLanguageDropdown',
    components: {
        LanguageDropdown,
        AudioInputDropdown,
        RecordButton
    },
    props: {
        sourceLanguage: {
            type: String,
            required: true
        },
        targetLanguage: {
            type: String,
            required: true
        },
        inputDevice: {
            type: Number,
        },
        isListening: Boolean,
        isConnecting: Boolean,
        connectFailed: Boolean,
        errorMessage: String,
        hasConnection: Boolean,
        speakingLanguages: Array,
        languages: Array,
        qrCodeUrl: String,
        shareUrl: String,
        smoothScrollEnabled: {
            type: Boolean,
            default: true
        },
        diarizationEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isCopied: false,
            mdiContentCopy,
            mdiCheck,
            mdiLink,
            mdiOpenInNew
        };
    },
    emits: [
        'update:sourceLanguage',
        'update:targetLanguage',
        'update:inputDevice',
        'start-listening',
        'stop-listening',
        'retry-connection',
        'update:smoothScrollEnabled',
        'update:diarizationEnabled'
    ],
    methods: {
        handleButtonClick() {
            if (this.isListening) {
                this.$emit('stop-listening');
            } else {
                this.$emit('start-listening');
            }
        },
        copyUrl() {
            navigator.clipboard.writeText(this.shareUrl)
                .then(() => {
                    this.isCopied = true;
                    setTimeout(() => {
                        this.isCopied = false;
                    }, 1000);
                })
                .catch((err) => {
                    console.error('Failed to copy URL:', err);
                }, );
        },
        openUrl() {
            window.open(this.shareUrl, '_blank');
        }
    }
};
</script>

<style scoped>
.error-message {
    width: 100%;
    padding: 12px;
    background-color: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.retry-button {
    background-color: #721c24;
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
}

.control-container {
    display: flex;
    flex-direction: row;
    padding: 0;
    gap: 6px;
    width: 100%;
}

.mic-selection {
    flex: 1;
    align-items: center;
    justify-self: center;
}

.qr-code-container {
    width: 100%;
    text-align: center;
}

@media screen and (max-width: 548px) {
    .qr-code {
        display: none;
    }
}

.qr-code {
    width: 200px;
    height: 200px;
    margin-top: 32px;
    border: 1px solid var(--text-color);
    border-radius: 5px;
}

.url-container {
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    overflow: hidden;
}

.url-input {
    font-family: montserrat;
    flex: 1;
    padding: 8px;
    font-size: 14px;
    height: 28px;
    min-width: 0;
    color: rgba(var(--text-color-rgb), 0.1);
    background-color: var(--container-color);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    outline: none;
    cursor: default;
    text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-button {
    height: 46px;
    padding: 8px;
    font-family: montserrat;
    font-size: 14px;
    font-weight: bold;
    color: var(--text-color);
    background-color: var(--container-color);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.copy-button:hover {
    background-color: var(--primary-hover);
}

.toggles-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 12px 0;
    width: 100%;
}

.smooth-scroll-toggle {
    display: flex;
    justify-content: flex-start;
    align-items: center; 
}

.diarization-toggle {
    display: flex;
    justify-content: flex-start;
    align-items: center;
}

.toggle-container {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 18px;
    line-height: 18px;
}

.toggle-label {
    font-family: 'Montserrat', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-color);
    opacity: 0.8;
    display: flex;
    align-items: center;
    height: 18px;
    margin-left: 2px;
}

.circle-checkbox {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 18px;
    cursor: pointer;
}

.circle-checkbox input {
    opacity: 0;
    width: 0;
    height: 0;
}

.checkmark {
    position: absolute;
    top: 0;
    left: 0;
    width: 18px;
    height: 18px;
    background-color: var(--container-color);
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    transition: all 0.2s ease-in-out;
}

.checkmark:after {
    content: "";
    position: absolute;
    display: none;
    left: 6px;
    top: 3px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.circle-checkbox input:checked + .checkmark {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
}

.circle-checkbox input:checked + .checkmark:after {
    display: block;
}

.checkmark:hover {
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.15);
    border-color: var(--primary-color);
}

@media screen and (max-width: 768px) {
    .qr-code {
        width: 100px;
        height: 100px;
    }

    .url-input {
        font-size: 12px;
        padding: 6px;
    }

    .copy-button {
        padding: 6px 10px;
        font-size: 12px;
    }

    .toggles-row {
        flex-direction: column;
        gap: 8px;
        align-items: stretch;
    }

    .smooth-scroll-toggle,
    .diarization-toggle {
        justify-content: flex-start;
    }
}
</style>
